import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  Grid as MuiGrid
} from '@mui/material';
import {
  VerifiedUser,
  Description,
  ExpandMore,
  Download,
  Visibility,
  TrendingUp,
  Warning,
  CheckCircle,
  Error,
  Info
} from '@mui/icons-material';
import { useTranslation } from '../i18n';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

interface ComplianceMetric {
  id: string;
  metric: string;
  value: string;
  status: 'compliant' | 'non-compliant' | 'warning';
  lastUpdated: string;
  description: string;
}

interface RegulatoryReport {
  id: string;
  reportType: string;
  period: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  submissionDate: string;
  dueDate: string;
  complianceScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  reviewer: string;
  comments: string;
}

interface AuditLogEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  details: string;
  ipAddress: string;
  status: 'success' | 'failure' | 'warning';
}

const mockComplianceMetrics: ComplianceMetric[] = [
  {
    id: '1',
    metric: 'Capital Adequacy Ratio',
    value: '15.2%',
    status: 'compliant',
    lastUpdated: '2024-01-15',
    description: 'Minimum requirement: 10.5%, Current: 15.2%'
  },
  {
    id: '2',
    metric: 'Liquidity Coverage Ratio',
    value: '125%',
    status: 'compliant',
    lastUpdated: '2024-01-15',
    description: 'Minimum requirement: 100%, Current: 125%'
  },
  {
    id: '3',
    metric: 'Non-Performing Loans',
    value: '3.8%',
    status: 'warning',
    lastUpdated: '2024-01-15',
    description: 'Target: <3%, Current: 3.8%'
  },
  {
    id: '4',
    metric: 'Customer Due Diligence',
    value: '98.5%',
    status: 'compliant',
    lastUpdated: '2024-01-15',
    description: 'Target: >95%, Current: 98.5%'
  },
  {
    id: '5',
    metric: 'Transaction Monitoring',
    value: '92.3%',
    status: 'warning',
    lastUpdated: '2024-01-15',
    description: 'Target: >95%, Current: 92.3%'
  }
];

const mockRegulatoryReports: RegulatoryReport[] = [
  {
    id: '1',
    reportType: 'Quarterly Financial Report',
    period: 'Q4 2023',
    status: 'approved',
    submissionDate: '2024-01-10',
    dueDate: '2024-01-31',
    complianceScore: 94,
    riskLevel: 'low',
    reviewer: 'Central Bank',
    comments: 'All requirements met, approved without conditions'
  },
  {
    id: '2',
    reportType: 'Anti-Money Laundering Report',
    period: 'Q4 2023',
    status: 'submitted',
    submissionDate: '2024-01-15',
    dueDate: '2024-01-31',
    complianceScore: 87,
    riskLevel: 'medium',
    reviewer: 'Financial Intelligence Unit',
    comments: 'Under review, minor issues identified'
  },
  {
    id: '3',
    reportType: 'Risk Management Report',
    period: 'Q4 2023',
    status: 'draft',
    submissionDate: '-',
    dueDate: '2024-01-31',
    complianceScore: 0,
    riskLevel: 'high',
    reviewer: '-',
    comments: 'Draft in progress, high priority'
  }
];

const mockAuditLog: AuditLogEntry[] = [
  {
    id: '1',
    timestamp: '2024-01-15 14:30:00',
    user: 'compliance@finscore.ai',
    action: 'Report Submission',
    details: 'Submitted Q4 2023 AML Report',
    ipAddress: '192.168.1.100',
    status: 'success'
  },
  {
    id: '2',
    timestamp: '2024-01-15 13:45:00',
    user: 'auditor@finscore.ai',
    action: 'Report Review',
    details: 'Reviewed Capital Adequacy calculations',
    ipAddress: '192.168.1.101',
    status: 'success'
  },
  {
    id: '3',
    timestamp: '2024-01-15 12:20:00',
    user: 'risk@finscore.ai',
    action: 'Risk Assessment',
    details: 'Updated portfolio risk metrics',
    ipAddress: '192.168.1.102',
    status: 'warning'
  }
];

const mockComplianceTrends = [
  { month: 'Jan', score: 92, target: 90 },
  { month: 'Feb', score: 89, target: 90 },
  { month: 'Mar', score: 91, target: 90 },
  { month: 'Apr', score: 93, target: 90 },
  { month: 'May', score: 88, target: 90 },
  { month: 'Jun', score: 94, target: 90 },
  { month: 'Jul', score: 90, target: 90 },
  { month: 'Aug', score: 92, target: 90 },
  { month: 'Sep', score: 89, target: 90 },
  { month: 'Oct', score: 91, target: 90 },
  { month: 'Nov', score: 93, target: 90 },
  { month: 'Dec', score: 95, target: 90 }
];

const mockRiskDistribution = [
  { name: 'Low Risk', value: 45, color: '#4caf50' },
  { name: 'Medium Risk', value: 35, color: '#ff9800' },
  { name: 'High Risk', value: 20, color: '#f44336' }
];

const RegulatoryReports: React.FC = () => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState('Q4 2023');
  const [selectedReport, setSelectedReport] = useState<RegulatoryReport | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

  // Add debugging
  console.log('RegulatoryReports component rendered');
  console.log('Translation function:', t);
  console.log('Mock data length:', mockRegulatoryReports.length);

  const overallComplianceScore = useMemo(() => {
    const scores = mockComplianceMetrics
      .filter(metric => metric.status === 'compliant')
      .map(() => 100);
    const warnings = mockComplianceMetrics
      .filter(metric => metric.status === 'warning')
      .map(() => 75);
    const nonCompliant = mockComplianceMetrics
      .filter(metric => metric.status === 'non-compliant')
      .map(() => 0);
    
    const allScores = [...scores, ...warnings, ...nonCompliant];
    return allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0;
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
      case 'approved':
        return 'success';
      case 'warning':
      case 'submitted':
        return 'warning';
      case 'non-compliant':
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
      case 'approved':
        return <CheckCircle fontSize="small" />;
      case 'warning':
      case 'submitted':
        return <Warning fontSize="small" />;
      case 'non-compliant':
      case 'rejected':
        return <Error fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Add click handler debugging
  const handleReportClick = (report: RegulatoryReport) => {
    console.log('Report clicked:', report);
    setSelectedReport(report);
  };

  const handleMetricClick = (metric: ComplianceMetric) => {
    console.log('Metric clicked:', metric);
    setSelectedReport({
      id: metric.id,
      reportType: metric.metric,
      period: selectedPeriod,
      status: metric.status as any,
      submissionDate: metric.lastUpdated,
      dueDate: '-',
      complianceScore: 0,
      riskLevel: 'low',
      reviewer: '-',
      comments: metric.description
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('regulatoryReports.title')}
      </Typography>

      {/* Compliance Overview */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <VerifiedUser color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">{t('regulatoryReports.overallScore')}</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {overallComplianceScore}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('regulatoryReports.complianceLevel')}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Description color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">{t('regulatoryReports.totalReports')}</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {mockRegulatoryReports.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('regulatoryReports.activeReports')}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUp color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">{t('regulatoryReports.compliantMetrics')}</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {mockComplianceMetrics.filter(m => m.status === 'compliant').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('regulatoryReports.outOfTotal', { total: mockComplianceMetrics.length })}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          select
          label={t('regulatoryReports.period')}
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="Q4 2023">Q4 2023</MenuItem>
          <MenuItem value="Q3 2023">Q3 2023</MenuItem>
          <MenuItem value="Q2 2023">Q2 2023</MenuItem>
          <MenuItem value="Q1 2023">Q1 2023</MenuItem>
        </TextField>
        
        <Button
          variant="contained"
          startIcon={<Download />}
          sx={{ ml: 'auto' }}
        >
          {t('regulatoryReports.exportAll')}
        </Button>
      </Box>

      {/* Charts Section */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {/* Compliance Trends */}
        <Paper sx={{ p: 2, flex: 1, minWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            {t('regulatoryReports.complianceTrends')}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mockComplianceTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <RechartsTooltip />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#2196f3"
                strokeWidth={2}
                name={t('regulatoryReports.actualScore')}
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke="#ff9800"
                strokeWidth={2}
                strokeDasharray="5 5"
                name={t('regulatoryReports.targetScore')}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Risk Distribution */}
        <Paper sx={{ p: 2, flex: 1, minWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            {t('regulatoryReports.riskDistribution')}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={mockRiskDistribution}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {mockRiskDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Regulatory Reports Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            {t('regulatoryReports.regulatoryReports')}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('regulatoryReports.reportType')}</TableCell>
                <TableCell>{t('regulatoryReports.period')}</TableCell>
                <TableCell>{t('regulatoryReports.status')}</TableCell>
                <TableCell>{t('regulatoryReports.complianceScore')}</TableCell>
                <TableCell>{t('regulatoryReports.riskLevel')}</TableCell>
                <TableCell>{t('regulatoryReports.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mockRegulatoryReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.reportType}</TableCell>
                  <TableCell>{report.period}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(report.status)}
                      label={t(`regulatoryReports.statuses.${report.status}`)}
                      color={getStatusColor(report.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={report.complianceScore >= 90 ? 'success.main' : 'warning.main'}
                    >
                      {report.complianceScore}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`regulatoryReports.riskLevels.${report.riskLevel}`)}
                      color={getStatusColor(report.riskLevel) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('regulatoryReports.view')}>
                        <IconButton
                          size="small"
                          onClick={() => handleReportClick(report)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('regulatoryReports.download')}>
                        <IconButton size="small">
                          <Download />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Compliance Metrics Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            {t('regulatoryReports.complianceMetrics')}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('regulatoryReports.metric')}</TableCell>
                <TableCell>{t('regulatoryReports.value')}</TableCell>
                <TableCell>{t('regulatoryReports.status')}</TableCell>
                <TableCell>{t('regulatoryReports.lastUpdated')}</TableCell>
                <TableCell>{t('regulatoryReports.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mockComplianceMetrics.map((metric) => (
                <TableRow key={metric.id}>
                  <TableCell>{metric.metric}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {metric.value}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(metric.status)}
                      label={t(`regulatoryReports.statuses.${metric.status}`)}
                      color={getStatusColor(metric.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{metric.lastUpdated}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleMetricClick(metric)}
                    >
                      {t('regulatoryReports.viewDetails')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detailed Report View */}
      {selectedReport && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {t('regulatoryReports.reportDetails')}: {selectedReport.reportType}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setSelectedReport(null)}
            >
              {t('regulatoryReports.close')}
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('regulatoryReports.period')}
              </Typography>
              <Typography variant="body1">{selectedReport.period}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('regulatoryReports.status')}
              </Typography>
              <Chip
                icon={getStatusIcon(selectedReport.status)}
                label={t(`regulatoryReports.statuses.${selectedReport.status}`)}
                color={getStatusColor(selectedReport.status) as any}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('regulatoryReports.complianceScore')}
              </Typography>
              <Typography variant="h6" color="primary">
                {selectedReport.complianceScore}%
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('regulatoryReports.comments')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {selectedReport.comments}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Download />}
            >
              {t('regulatoryReports.downloadReport')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Description />}
            >
              {t('regulatoryReports.viewFullReport')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Audit Log */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            {t('regulatoryReports.auditLog')}
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {mockAuditLog.map((entry) => (
            <Box key={entry.id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {entry.action}
                </Typography>
                <Chip
                  icon={getStatusIcon(entry.status)}
                  label={t(`regulatoryReports.statuses.${entry.status}`)}
                  color={getStatusColor(entry.status) as any}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {entry.timestamp} • {entry.user} • {entry.ipAddress}
              </Typography>
              <Typography variant="body2">
                {entry.details}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default RegulatoryReports;
